import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Modal } from '../../../UI/Modal/Modal'
import { ImagesTab } from './ImagesTab'
import { DocumentsTab } from './DocumentsTab'
import { UploadDocumentModal } from '../../../DocumentLibrary/UploadDocumentModal'
import FbButton from '../../../FbUI/FbButton'

type TabType = 'images' | 'documents'

interface ManageProductFilesModalProps {
  show: boolean
  onClose: () => void
  variantId: number
  variantName: string
  productId?: number | null
  productName?: string | null
}

export function ManageProductFilesModal({
  show,
  onClose,
  variantId,
  variantName,
  productId,
  productName,
}: ManageProductFilesModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('images')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const queryClient = useQueryClient()

  const tabs: { key: TabType; title: string }[] = [
    { key: 'images', title: 'Images' },
    { key: 'documents', title: 'Documents' },
  ]

  const handleUploadSuccess = () => {
    // Invalidate the variant images query to refresh the images list
    queryClient.invalidateQueries({ queryKey: ['variant-images', variantId] })
    // Invalidate all document table queries (including the modal variant table)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.startsWith('DOCUMENTS_') ||
            firstKey.startsWith('documents-modal-'))
        )
      },
    })
  }

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(open) => !open && onClose()}
        title={`Manage Files - ${variantName}`}
        description="View and manage images and documents linked to this variant"
        size="2xl"
        hideCancelButton
        onAccept={onClose}
        acceptButtonText="Close"
        headerExtra={
          activeTab === 'documents' ? (
            <FbButton
              type="button"
              size="sm"
              className="h-9 self-start"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus size={16} />
              Upload Document
            </FbButton>
          ) : undefined
        }
      >
        <div className="min-h-[400px]">
          {/* Tab navigation - left aligned to match settings structure */}
          <div className="border-b border-gray-200 mb-4">
            <div className="flex gap-0">
              {tabs.map(({ key, title }) => {
                const isActive = activeTab === key
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`
                      px-4 py-3 text-base relative transition-colors duration-200
                      ${
                        isActive
                          ? 'text-[#191D23] font-semibold border-b-2 border-green-700'
                          : 'text-[#191D23] font-light hover:text-[#191D23]'
                      }
                    `}
                  >
                    {title}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Tab content */}
          {activeTab === 'images' && (
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <ImagesTab variantId={variantId} />
            </div>
          )}
          {activeTab === 'documents' && <DocumentsTab variantId={variantId} />}
        </div>
      </Modal>

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        preSelectedProductId={productId ?? undefined}
        preSelectedProductName={productName ?? undefined}
        preSelectedVariantId={variantId}
        preSelectedVariantName={variantName}
        lockProductVariant
        onSuccess={handleUploadSuccess}
      />
    </>
  )
}
